/**
 * kgob.com/events (spec §4): the landing, the featured hero and, with `?e={slug}`, the share view where that
 * event leads the hero. Data comes from the backend's /api/events (5-minute timer, last-good) and, on first
 * paint, from the postbuild snapshot in window.__EVENTS_FEATURED__. Head tags are set at runtime through the
 * site's helmet shim (K-4/K-5; link-preview bots use the backend's /api/events/share/{slug} instead).
 * No personalisation (K-12). Renders the site's Header and Footer itself, as every page on the site does.
 */
import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Hero } from "../components/Hero";
import { EventsHead } from "../components/EventsHead";
import { ContactBand, Faq, HowItWorks, WhyCome } from "../components/Sections";
import { EventList } from "../components/EventList";
import { Notice } from "../components/Notice";
import { GateTransition } from "../components/GateTransition";
import { useViewTime } from "../components/AvailabilityRecheck";
import { useCatalog, useFeatured } from "../lib/catalog";
import { buildHero } from "../lib/hero";
import { buildList } from "../lib/list";
import { captureAttribution } from "../lib/attribution";
import { isSlug } from "../lib/gate";
import { trackPage } from "../lib/analytics";
import "../events.css";

/** The id the postbuild gives the crawler markup it injects into #root (the site wipes it on hydration). */
export const SEO_CONTENT_ID = "seo-content";

export default function EventsLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const e = params.get("e");
  const list = useFeatured();
  const catalog = useCatalog();
  const now = useViewTime();
  const legacyShare = !!e && isSlug(e);
  const lead = null;   // ?e= is resolved by /events/{slug} now; the landing keeps its own head tags
  const hero = useMemo(() => buildHero(list, lead, now, catalog), [list, lead, now, catalog]);
  const browse = useMemo(() => buildList(catalog, now), [catalog, now]);

  // `?e={slug}` is a legacy deep link: replace it with the event's own page (WHO-OWNS-WHICH-URL.md §3, "old
  // share cards are in the wild"). The other parameters ride along so attribution survives, and a retired slug
  // is resolved one hop later by the page itself, which holds the payload.
  useEffect(() => {
    if (!legacyShare) return;
    const next = new URLSearchParams(params);
    next.delete("e");
    const search = next.toString();
    navigate({ pathname: `/events/${e}`, search: search ? `?${search}` : "" }, { replace: true });
  }, [legacyShare, e, params, navigate]);

  // The baked crawler markup goes as soon as the module is up (the snapshot it carried has been read already);
  // then attribution on arrival (§5.3) and the page view.
  useEffect(() => {
    document.getElementById(SEO_CONTENT_ID)?.remove();
    captureAttribution();
    trackPage({ path: "/events" });
  }, []);

  return (
    <>
      {/* Head tags for humans and Google (K-4, K-5): the leading event's, else the landing's. */}
      <EventsHead lead={lead} shown={hero.shown} now={now} />
      <Header />
      <div className="ev-root">
        <main id="ev-main">
          <Notice />
          <Hero hero={hero} />
          {/* Every published event, reachable without a token: the hero only ever shows the featured few. */}
          <EventList list={browse} />
          <WhyCome />
          <HowItWorks />
          <Faq />
          <ContactBand />
        </main>
        <GateTransition />
      </div>
      <Footer />
    </>
  );
}
